import { useState } from "react";
import { History } from "lucide-react";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  SearchableMultiSelect,
  type MultiSelectGroup,
} from "@/components/ui/searchable-multi-select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { APPT_SLUGS, SLUG_TO_GROUP, humanizeSlug } from "@/lib/metrics";
import type { FilterState, ScopeFilters, ScopeOptions } from "@/types/filters";

const RECENT_SITES_KEY = "fetchtime.recentSites";
const RECENT_SITES_MAX = 4;

interface RecentSite {
  id: string;
  name: string;
}

function loadRecentSites(): RecentSite[] {
  try {
    return JSON.parse(sessionStorage.getItem(RECENT_SITES_KEY) ?? "[]") as RecentSite[];
  } catch {
    return [];
  }
}

const APPT_TYPE_GROUPS: MultiSelectGroup[] = (() => {
  const map = new Map<string, Array<{ value: string; label: string }>>();
  for (const slug of APPT_SLUGS) {
    const group = SLUG_TO_GROUP[slug] ?? "Other";
    if (!map.has(group)) map.set(group, []);
    map.get(group)!.push({ value: slug, label: humanizeSlug(slug) });
  }
  return Array.from(map.entries()).map(([heading, options]) => ({ heading, options }));
})();

interface ScopeBarProps {
  filters: FilterState;
  options: ScopeOptions;
  setScope: (patch: Partial<ScopeFilters>) => void;
  resetScope: () => void;
  setAppointmentTypes: (slugs: string[]) => void;
  setExcludeZeroApptDays: (v: boolean) => void;
}

export function ScopeBar({
  filters,
  options,
  setScope,
  resetScope,
  setAppointmentTypes,
  setExcludeZeroApptDays,
}: ScopeBarProps) {
  const { appointmentTypes } = filters;
  const hasActiveFilters =
    Object.values(filters.scope).some((v) => v !== null) || appointmentTypes.length > 0;

  const [recentSites, setRecentSites] = useState<RecentSite[]>(loadRecentSites);
  const [lastTrackedSiteId, setLastTrackedSiteId] = useState<string | null>(null);

  // Track selected sites for the quick-switch chips (session-scoped). Runs as a
  // render-phase state adjustment (not an effect) per React's derived-state pattern.
  //
  // The id is marked as tracked only once its NAME has been found, which is the
  // whole reason this works. Marking it first meant that changing the scope --
  // which puts the payload back in flight, so options.sites is momentarily
  // empty -- burned the id on a render that could not name it, and the site was
  // never recorded once the options did arrive. That silently emptied the
  // Recent row: it kept whatever had been captured by luck and recorded nothing
  // new. Leaving the id untracked costs one failed lookup per render until the
  // options land, and no state is set on those renders, so there is no loop.
  const selectedSiteId = filters.scope.locationId;
  if (selectedSiteId && selectedSiteId !== lastTrackedSiteId) {
    const name =
      options.sites.find((s) => s.id === selectedSiteId)?.name ??
      options.otherSites.find((s) => s.id === selectedSiteId)?.name;
    if (name) {
      setLastTrackedSiteId(selectedSiteId);
      const next = [
        { id: selectedSiteId, name },
        ...recentSites.filter((s) => s.id !== selectedSiteId),
      ].slice(0, RECENT_SITES_MAX);
      sessionStorage.setItem(RECENT_SITES_KEY, JSON.stringify(next));
      setRecentSites(next);
    }
  }

  const recentChips = recentSites.filter((s) => s.id !== selectedSiteId);

  // Dynamic group headings — name the narrowest filter that's constraining the
  // list, e.g. "In River Valley" / "Other sites".
  const { scope } = filters;
  const dvmName = scope.resourceId
    ? (options.dvms.find((d) => d.id === scope.resourceId)?.name ?? scope.resourceId)
    : null;
  const siteScopeLabel =
    dvmName ?? scope.divisionName ?? scope.groupName ?? scope.countryCode ?? null;

  const siteOptions = options.sites.map((s) => ({ value: s.id, label: `${s.id} - ${s.name}` }));
  const otherSiteOptions = options.otherSites.map((s) => ({
    value: s.id,
    label: `${s.id} - ${s.name}`,
  }));
  const sitesGrouped = otherSiteOptions.length > 0 && siteScopeLabel !== null;

  return (
    <div className="bg-[color-mix(in_srgb,var(--brand-cream)_60%,white)] rounded-xl px-4 py-2.5 mb-3 border border-black/5">
      {/* Dropdowns */}
      <div className="flex flex-col gap-2 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-semibold uppercase tracking-[0.6px] text-gray-500 min-w-[52px]">
            Scope
          </span>

          <SearchableSelect
            value={filters.scope.countryCode ?? "__all"}
            onValueChange={(v) => setScope({ countryCode: v === "__all" ? null : v })}
            placeholder="All countries"
            options={[
              { value: "__all", label: "All countries" },
              ...options.countries.map((c) => ({ value: c, label: c })),
            ]}
          />

          <SearchableSelect
            value={filters.scope.groupName ?? "__all"}
            onValueChange={(v) => setScope({ groupName: v === "__all" ? null : v })}
            placeholder="All groups"
            options={[
              { value: "__all", label: "All groups" },
              ...options.groups.map((g) => ({ value: g, label: g })),
            ]}
          />

          <SearchableSelect
            value={filters.scope.divisionName ?? "__all"}
            onValueChange={(v) => setScope({ divisionName: v === "__all" ? null : v })}
            placeholder="All divisions"
            options={[
              { value: "__all", label: "All divisions" },
              ...options.divisions.map((d) => ({ value: d, label: d })),
            ]}
          />

          <SearchableSelect
            value={filters.scope.locationId ?? "__all"}
            onValueChange={(v) => setScope({ locationId: v === "__all" ? null : v })}
            placeholder="All sites"
            triggerWidth="200px"
            dropdownWidth="320px"
            options={sitesGrouped ? [] : [{ value: "__all", label: "All sites" }, ...siteOptions]}
            extraGroups={
              sitesGrouped
                ? [
                    {
                      heading: `In ${siteScopeLabel}`,
                      options: [{ value: "__all", label: "All sites" }, ...siteOptions],
                    },
                    { heading: "Other sites", options: otherSiteOptions },
                  ]
                : undefined
            }
          />

          <SearchableSelect
            value={filters.scope.resourceId ?? "__all"}
            onValueChange={(v) => setScope({ resourceId: v === "__all" ? null : v })}
            placeholder="All DVMs"
            options={[
              { value: "__all", label: "All DVMs" },
              ...options.dvms.map((d) => ({ value: d.id, label: d.name })),
            ]}
          />

          <SearchableMultiSelect
            values={appointmentTypes}
            onValuesChange={setAppointmentTypes}
            placeholder="All appt types"
            countLabel="types"
            groups={APPT_TYPE_GROUPS}
            dropdownWidth="260px"
          />

          <ToggleGroup
            type="single"
            variant="outline"
            value={filters.excludeZeroApptDays ? "on" : ""}
            onValueChange={(v) => setExcludeZeroApptDays(v === "on")}
          >
            <ToggleGroupItem
              value="on"
              className="h-auto text-[12px] px-3 py-1 border-black/15 bg-white text-gray-600 font-normal hover:bg-gray-50 hover:text-gray-600 transition-colors data-[state=on]:bg-[var(--brand-dark-teal)] data-[state=on]:text-white data-[state=on]:border-[var(--brand-dark-teal)]"
            >
              Exclude 0 appt days
            </ToggleGroupItem>
          </ToggleGroup>

          {hasActiveFilters && (
            <button
              onClick={() => {
                resetScope();
                setAppointmentTypes([]);
              }}
              className="text-[11px] px-2.5 py-1 rounded-md border border-black/15 bg-white text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Reset
            </button>
          )}
        </div>

        {recentChips.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.6px] text-gray-400 min-w-[52px]">
              <History className="h-3 w-3" />
              Recent
            </span>
            {recentChips.map((s) => (
              <button
                key={s.id}
                onClick={() => setScope({ locationId: s.id })}
                title={`Switch to ${s.id} - ${s.name}`}
                className="text-[11px] px-2.5 py-1 rounded-full border border-black/15 bg-white text-gray-500 hover:text-[var(--brand-dark-teal)] hover:border-[var(--brand-dark-teal)] transition-colors max-w-[220px] truncate"
              >
                {s.id} - {s.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
